import React, {useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useAppState, useDsManager, useMobileNavController, useRootStore} from "@stores/AppStateContext";

import {isFeatureFlagEnabled} from "@stores/featureFlags";

import {PageName} from "@stores/ViewStore";

import {RootStoreModel} from "@stores/RootStore";

import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import {WorkspaceSelect} from "../../components/WorkspaceSelect/WorkspaceSelect";
import {WorkspaceAvatar} from "../../components/Avatar/Avatar";
import {UpgradePlanCard} from "./UpgradePlanCard";
import {isFreeTrial} from "@stores/client";
import {MainSidebar as MainSidebarView, MainSidebarItem} from "../../components/MainSidebar/MainSidebar";
import {MainSidebarHeader as MainSidebarHeaderView} from "../../components/MainSidebarHeader/MainSidebarHeader";

import {NAVIGATION_ITEMS, NavigationItem, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED} from './navigationConfig';

export const MainSidebar = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const mobileNav = useMobileNavController();
    const isCollapsed = rootStore.isSidebarCollapsed;

    const activePage = rootStore.view.page as PageName;
    const isMobile = screenSizeProvider.view !== 'desktop';

    const handleNav = (page: PageName | 'help-external') => () => {
        if (page === 'help-external') {
            window.open('/help', '_blank');
            return;
        }
        rootStore.view.openPageByName(page);
        if (isMobile) {
            // reaction doesn't work if page is not changed.
            mobileNav.ensureLayerForCurrentContext(page);
        }
    };

    const client = dsManager?.clientAR?.row;
    const currentWsId = rootStore.view.workspace?.id;
    const assignmentDS = currentWsId ? dsManager?.getAuditAssignmentDS(currentWsId) : undefined;
    const isAdmin = Boolean(appState.userState?.isCurrentUserAdmin);

    useEffect(() => {
        if (!isAdmin && assignmentDS && !assignmentDS.isInitialized) {
            void assignmentDS.initialLoad();
        }
    }, [assignmentDS, isAdmin]);

    const hasActiveAuditAssignments = Boolean(assignmentDS?.activeAssignments.length);
    const isVisible = (item: NavigationItem) => {
        if (item.id === 'audits') {
            return isAdmin || hasActiveAuditAssignments;
        }
        return !item.featureFlag || isFeatureFlagEnabled(client as any, item.featureFlag);
    };

    const tools = NAVIGATION_ITEMS.filter(i => i.section === 'tools' && isVisible(i));
    const settings = NAVIGATION_ITEMS.filter(i => i.section === 'settings' && isVisible(i));

    if (isMobile && !mobileNav.showMainSidebar) return null;

    const mapItems = (items: NavigationItem[]): MainSidebarItem[] => items.map((item) => ({
        id: item.id,
        label: item.label,
        Icon: item.icon,
        isActive: item.subPages.includes(activePage),
        onClick: handleNav(item.page),
    }));

    return (
        <MainSidebarView
            isCollapsed={isCollapsed}
            isOverlay={isMobile}
            header={!isMobile ? <MainSidebarHeader/> : undefined}
            tools={mapItems(tools)}
            settings={mapItems(settings)}
            upgradeCard={rootStore.isReady && isFreeTrial(appState.userState!.clientDB!) ? <UpgradePlanCard/> : undefined}
        />
    );
});

export const MainSidebarHeader = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const isCollapsed = rootStore.isSidebarCollapsed;

    return (
        <MainSidebarHeaderView
            isCollapsed={isCollapsed}
            workspaceSelect={<WorkspaceSelect/>}
            workspaceAvatar={appState.currentWorkspaceDb ? (
                <WorkspaceAvatar
                    workspace={appState.currentWorkspaceDb!}
                    size={32}
                    className={'MainSidebar__wsAvatar'}
                />
            ) : undefined}
            collapseLabel={'Collapse sidebar'}
            expandLabel={'Expand sidebar'}
            onCollapse={() => rootStore.toggleIsSidebarCollapsed(true)}
            onExpand={() => rootStore.toggleIsSidebarCollapsed(false)}
        />
    )
})

export const getSidebarCurrentWidth = (rootStore: RootStoreModel) => {
    return rootStore.isSidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
}
